// The frame loop and the instrumentation (the plan's A.2 `frame.ts` and A.8).
//
// One `requestAnimationFrame` loop, capped at 60 by an accumulator rather
// than by a timer, so a 120 Hz display draws every second callback and a
// slow frame does not build a backlog. The page's visibility starts and
// stops it — visibility and never focus, because a window behind another
// window still presents frames and a hidden tab does not (R190).
//
// The cover state of the chrome sets the cap (R1755, ui-map-interface.md
// §10.5): 60 while nothing covers the map, 20 under the full telephone
// sheet, and a pause under the opaque telephone OPTIONS menu. A paused
// loop asks for no animation frame; a timer runs its one-second sweep.
//
// The order inside a frame is fixed: build the camera once, `update` every
// visible layer, upload the data textures that went dirty, set the viewport
// and clear, then `draw` every visible layer. An invisible layer skips both
// passes (ui-map-interface.md §10.3).

import type { Backend, FrameGfx } from './gfx/types';
import type { DrawCtx, FrameInfo, FrameStats, Layer, LayerStats, ViewRect } from './layer';
import { View } from './view';
import { cssRgb } from './gfx/worldquad';
import { FRAME_LOOP, WATER_DEEP_LOW } from '../gen/presentation';
import { grownView } from './layers/shared';
import { onKnobs } from './knobs';
import type { Cover } from '../ui/overlay/types';

/** The cap. The map is a strategy view; nothing on it needs 120 Hz. */
export const MAX_FPS = FRAME_LOOP.maxFps;

/** The cap while the telephone sheet stands at its full height (R1755). */
export const SHEET_FPS = FRAME_LOOP.sheetFps;

/**
 * The cap the headroom run asks for (`?uncap=1`, plan C.7).
 *
 * The loop paces itself with a period, so "no cap" is a period short enough
 * that no display can outrun it rather than a zero, which would be an
 * infinite period and no frames at all.
 */
export const UNCAPPED_FPS = 1000;

/** The span of the one-second sweep, and of the idle sweep of a paused
 *  loop. */
const SWEEP_MS = 1000;

/**
 * The frame period of a cover state, in ms, or null for a paused map
 * (R1755, ui-map-interface.md §10.5). The opaque telephone menu hides every
 * pixel of the map, so the map draws nothing under it. The full sheet
 * covers the map at an alpha of 0.9 (R1748), so a third of the frames is
 * enough. `uncap` is the headroom run of `?uncap=1`: it lifts both caps,
 * and the menu still pauses, because there is nothing to measure under it.
 */
export function framePeriod(cover: Cover, uncap = false): number | null {
  if (cover === 'menu') return null;
  if (uncap) return 1000 / UNCAPPED_FPS;
  return 1000 / (cover === 'sheet' ? SHEET_FPS : MAX_FPS);
}

/** The layer that paints the whole screen opaque. `#clear` and `#blend` read it. */
const BACKGROUND_LAYER = 'background';

/**
 * The frame clock, picked once: `performance` is absent in a node test, and
 * the loop reads the clock several times a frame.
 */
const clock: () => number = typeof performance !== 'undefined'
  ? () => performance.now()
  : () => Date.now();

/**
 * When the next frame is due.
 *
 * A frame that arrives on time pushes the deadline by exactly one period, so
 * the cap does not drift. A frame that arrives a whole period late resets
 * the deadline to now, so a stall does not leave a backlog of due frames
 * that all draw at once.
 */
export function advanceDue(due: number, now: number, period: number): number {
  const next = due + period;
  return next <= now ? now + period : next;
}

/** How the frame loop reports a second's worth of work. */
export type StatsSink = (line: string, stats: FrameStats) => void;

/**
 * The four points of a frame the shared perf harness measures (R431, plan
 * C.7). `start` is the top, `update` and `draw` close the two passes, and
 * `end` is the bottom, after the last draw call was submitted.
 *
 * The hook exists so the harness stays engine-agnostic: `fast/main.ts`
 * translates these four names into `frameStart` / `phase` / `frameEnd`, the
 * same way the PixiJS ticker does at its own call sites. Without `?perf=1`
 * no sink is installed and the loop pays one null check a frame.
 */
export type FramePhase = 'start' | 'update' | 'draw' | 'end';

export interface LoopOpts {
  gfx: FrameGfx;
  view: View;
  /** The ordered layer slot table. The loop walks it as it is. */
  layers: Layer[];
  /** Runs between the update pass and the clear: dirty texture uploads. */
  beforeDraw?: (frame: FrameInfo) => void;
  onStats?: StatsSink;
  maxFps?: number;
  /**
   * How many clock ms one wall ms is worth: 1 by default. The picture baker
   * of MODDING runs at twice the menu rate with a scale of 2, so each frame
   * steps its clock as far as a frame of the menu preview does (R2084). At
   * 2 `dtMs` is the capped wall step times the scale, and `timeS` adds it;
   * at 1 both are what they always were. `frame.now` stays wall time.
   */
  clockScale?: number;
  /** The four frame marks the perf harness reads. Absent without `?perf=1`. */
  onPhase?: (phase: FramePhase) => void;
  /**
   * The client's own per-frame work, run at the top of every frame.
   *
   * The link banner is what needs it: a dropped socket has to show up on
   * the frame it dropped and not at the next one-second sweep (R108). It
   * is inside the measured frame, because it IS work the frame does.
   */
  onFrame?: (frame: FrameInfo) => void;
  /**
   * The chrome's share of `onFrame` while the map is paused (R1755): the
   * link line and its two events, which must not wait for the cover to
   * lift. It runs once a second, on the idle sweep.
   */
  onIdle?: () => void;
}

/** What `window.__fast` carries. `engine.ts` fills it. */
export interface FastDebug {
  engine: unknown;
  camera: unknown;
  layers: Layer[];
  stats: FrameStats;
  atlas: unknown;
  /** Which graphics API the picture is drawn through. */
  backend: Backend;
  loseContext(): void;
  restoreContext(): void;
}

export class Loop {
  readonly stats: FrameStats = {
    fps: 0, frameMs: 0, updateMs: 0, drawMs: 0, gpuMs: null,
    drawCalls: 0, instances: 0, perLayer: {}, contextLost: false,
  };

  readonly frame: FrameInfo;

  private raf = 0;
  private running = false;
  private period: number;
  private readonly clockScale: number;
  private due = 0;
  /** The map is paused under the opaque telephone menu (R1755). */
  private paused = false;
  /** The timer of the idle sweep, while a running loop is paused. */
  private idle: ReturnType<typeof setTimeout> | null = null;
  private last = 0;
  private startedAt = 0;
  private timeS = 0;
  private clearColor: [number, number, number];
  // The two measurement knobs of the frame (R431). `#clear=0` drops the
  // clear under an opaque background, `#blend=0` takes BLEND off for that
  // one pass. Both default to what the client ships, and both are read live
  // like every other knob, so an ablation costs no reload.
  private doClear = true;
  private doBlend = true;
  private readonly stopKnobs: () => void;

  // The one-second sweep.
  private sweepAt = 0;
  private frames = 0;
  private sumFrame = 0;
  private sumUpdate = 0;
  private sumDraw = 0;

  /**
   * What runs once, right after the next frame is submitted (R1623). The
   * drawing buffer of a WebGL2 canvas and the current texture of a WebGPU
   * canvas hold the picture until the task ends, so a copy taken here is
   * the picture on the glass in both backends. Photo mode and the REPORT
   * sheet copy the map this way.
   */
  private afterFrame: Array<() => void> = [];

  private readonly viewRect: ViewRect = { x0: 0, y0: 0, x1: 0, y1: 0 };
  private readonly cullRect: ViewRect = { x0: 0, y0: 0, x1: 0, y1: 0 };
  /** What every layer draws with. One object, rewritten every frame. */
  private readonly ctx: DrawCtx;

  constructor(private readonly opts: LoopOpts) {
    this.period = 1000 / (opts.maxFps ?? MAX_FPS);
    this.clockScale = opts.clockScale ?? 1;
    this.stopKnobs = onKnobs((k) => {
      this.doClear = k.clear === 1;
      this.doBlend = k.blend === 1;
    });
    this.clearColor = cssRgb(WATER_DEEP_LOW);
    this.frame = {
      dtMs: 0, timeS: 0, now: 0,
      view: this.viewRect, cull: this.cullRect,
      cam: opts.view.uniforms(opts.gfx.width, opts.gfx.height, opts.gfx.dpr),
    };
    this.ctx = { gfx: opts.gfx, cam: this.frame.cam, frame: this.frame, stats: this.stats, layer: '' };
    for (const l of opts.layers) this.slot(l.name);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibility);
    }
  }

  get isRunning(): boolean { return this.running; }

  /** True while the cover state pauses the map (R1755). */
  get isPaused(): boolean { return this.paused; }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.resume();
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    cancelAnimationFrame(this.raf);
    this.disarmIdle();
  }

  /**
   * Sets the frame period from the cover state (R1755, §10.5): a period in
   * ms (`framePeriod`), or null to pause the map. A shorter period lifts
   * the cap at once, because the next frame falls due now; the end of a
   * pause draws a frame at once. A hidden document still stops the loop
   * whatever the period says (R304), and a pause outlives a hidden spell.
   */
  setPeriod(ms: number | null): void {
    if (ms === null) {
      if (this.paused) return;
      this.paused = true;
      if (!this.running) return;
      cancelAnimationFrame(this.raf);
      this.armIdle();
      return;
    }
    const lift = ms < this.period;
    this.period = ms;
    if (this.paused) {
      this.paused = false;
      if (!this.running) return;
      this.disarmIdle();
      this.resume();
      return;
    }
    if (lift) this.due = Math.min(this.due, clock());
  }

  /**
   * Draws one frame of a paused map in the next animation frame: the
   * engine calls it when the surface changed size, because a resize clears
   * the canvas, and the strip of map under the tab bar must not go blank.
   */
  redraw(): void {
    if (!this.running || !this.paused) return;
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(this.drawOnce);
  }

  dispose(): void {
    this.stop();
    this.stopKnobs();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
  }

  /** Runs `fn` once, in the task of the next frame, after its submit. */
  afterNextFrame(fn: () => void): void {
    this.afterFrame.push(fn);
  }

  /** Adds a layer's stats slot. `engine.setLayer` calls it. */
  trackLayer(name: string): void {
    this.slot(name);
  }

  private onVisibility = () => {
    if (document.hidden) this.stop();
    else this.start();
  };

  /**
   * Starts the clocks again after a stop or a pause: the layer clock
   * stands still while nothing draws, as it does for a hidden tab, and a
   * running map draws its first frame at once. A paused map runs its idle
   * sweep instead.
   */
  private resume(): void {
    const now = clock();
    this.last = now;
    this.due = now;
    this.startedAt = now - this.timeS * 1000;
    this.sweepAt = now + SWEEP_MS;
    if (this.paused) this.armIdle();
    else this.raf = requestAnimationFrame(this.tick);
  }

  /** Arms the idle sweep of a paused map, one second from now. */
  private armIdle(): void {
    this.disarmIdle();
    this.idle = setTimeout(this.idleSweep, SWEEP_MS);
  }

  private disarmIdle(): void {
    if (this.idle === null) return;
    clearTimeout(this.idle);
    this.idle = null;
  }

  /**
   * The one-second sweep of a paused map (R1755). The statistics line, the
   * sound switches and the clock of the music player keep their second, and
   * the line says what the map does: no frame drew, so the figures of the
   * frame read 0. The chrome's own frame work runs here too (`onIdle`).
   */
  private idleSweep = () => {
    this.idle = null;
    if (!this.running || !this.paused) return;
    this.idle = setTimeout(this.idleSweep, SWEEP_MS);
    this.opts.onIdle?.();
    const stats = this.stats;
    stats.fps = 0;
    stats.frameMs = 0;
    stats.updateMs = 0;
    stats.drawMs = 0;
    stats.drawCalls = 0;
    stats.instances = 0;
    this.resetSums();
    this.opts.onStats?.(statsLine(stats, this.opts.view.scale.x, this.opts.gfx), stats);
  };

  /** The one frame `redraw` asks for. */
  private drawOnce = (now: number) => {
    if (!this.running || !this.paused) return;
    this.render(now);
  };

  private tick = (now: number) => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.tick);
    // Half a millisecond of slack: a display that fires at 16.666 ms must
    // not skip every other frame because of rounding.
    if (now + 0.5 < this.due) return;
    this.due = advanceDue(this.due, now, this.period);
    this.render(now);
  };

  private render(now: number): void {
    const gfx = this.opts.gfx;
    const stats = this.stats;
    stats.contextLost = gfx.lost;

    let dtMs = Math.min(now - this.last, 100);
    this.last = now;
    if (this.clockScale === 1) {
      this.timeS = (now - this.startedAt) / 1000;
    } else {
      // A scaled clock adds its steps, so a pause or a hidden spell moves
      // it by one capped step and not by the gap times the scale.
      dtMs *= this.clockScale;
      this.timeS += dtMs / 1000;
    }

    const frame = this.frame;
    frame.dtMs = dtMs;
    frame.now = now;
    frame.timeS = this.timeS;
    frame.cam = this.opts.view.uniforms(gfx.width, gfx.height, gfx.dpr);
    this.opts.view.viewRect(this.viewRect, gfx.width, gfx.height);
    grownView(this.cullRect, this.viewRect);

    const frameStart = clock();
    const onPhase = this.opts.onPhase;
    onPhase?.('start');
    this.opts.onFrame?.(frame);

    // 1. The update pass.
    for (const layer of this.opts.layers) {
      if (!layer.visible) continue;
      const slot = this.slot(layer.name);
      const t0 = clock();
      layer.update(frame);
      slot.updateMs = clock() - t0;
    }
    const updateEnd = clock();
    onPhase?.('update');

    // 2. Whatever the caller must upload before anything reads it.
    this.opts.beforeDraw?.(frame);

    if (gfx.lost) {
      onPhase?.('draw');
      onPhase?.('end');
      this.accumulate(frameStart, updateEnd, updateEnd, now);
      return;
    }

    // 3. The frame opens: the timer, the viewport and the clear.
    //
    // `#clear=0` drops the clear, and only where the background layer paints
    // the same pixels opaque a moment later. With that layer hidden the
    // clear is the only thing that fills the surface, so it always runs.
    gfx.beginFrame(this.doClear || !this.backgroundVisible(), this.clearColor);

    // 4. The draw pass.
    const drawStart = clock();
    const ctx = this.ctx;
    ctx.cam = frame.cam;
    for (const layer of this.opts.layers) {
      if (!layer.visible) {
        // A hidden layer submits nothing this frame, and its slot must say
        // so: the 1 Hz sweep sums every slot, and a layer that drew once
        // and then hid would otherwise inflate the total until a reload.
        const idle = stats.perLayer[layer.name];
        if (idle) { idle.drawCalls = 0; idle.instances = 0; }
        continue;
      }
      const slot = this.slot(layer.name);
      const dc = gfx.drawCalls;
      const inst = gfx.instances;
      ctx.layer = layer.name;
      // `#blend=0` takes BLEND off for the background pass alone. That pass
      // writes an opaque colour over the whole screen, so the blend unit
      // costs a read of a destination nothing needs. Every later pass keeps
      // the one blend state the backend sets.
      const unblended = !this.doBlend && layer.name === BACKGROUND_LAYER;
      if (unblended) gfx.setBlend(false);
      layer.draw(ctx);
      if (unblended) gfx.setBlend(true);
      slot.drawCalls = gfx.drawCalls - dc;
      slot.instances = gfx.instances - inst;
    }
    const drawEnd = clock();
    onPhase?.('draw');
    onPhase?.('end');
    gfx.endFrame();
    stats.gpuMs = gfx.gpuMs;
    if (this.afterFrame.length > 0) {
      for (const fn of this.afterFrame.splice(0)) {
        try { fn(); } catch (err) { console.error('fast: an after-frame copy threw', err); }
      }
    }

    this.accumulate(frameStart, updateEnd, drawStart, drawEnd);
  }

  /** Does the opaque background paint this frame? Only `#clear=0` asks. */
  private backgroundVisible(): boolean {
    for (const l of this.opts.layers) {
      if (l.name === BACKGROUND_LAYER) return l.visible;
    }
    return false;
  }

  /** Starts the sums of the next one-second sweep. */
  private resetSums(): void {
    this.frames = 0;
    this.sumFrame = 0;
    this.sumUpdate = 0;
    this.sumDraw = 0;
  }

  private slot(name: string): LayerStats {
    let s = this.stats.perLayer[name];
    if (!s) {
      s = { updateMs: 0, drawCalls: 0, instances: 0 };
      this.stats.perLayer[name] = s;
    }
    return s;
  }

  private accumulate(frameStart: number, updateEnd: number, drawStart: number, drawEnd: number): void {
    const stats = this.stats;
    this.frames++;
    this.sumUpdate += updateEnd - frameStart;
    this.sumDraw += drawEnd - drawStart;
    this.sumFrame += drawEnd - frameStart;

    const now = clock();
    if (now < this.sweepAt) return;
    const span = 1000 - (this.sweepAt - now);
    const n = Math.max(1, this.frames);
    stats.fps = Math.round((this.frames * 1000) / Math.max(1, span));
    stats.frameMs = this.sumFrame / n;
    stats.updateMs = this.sumUpdate / n;
    stats.drawMs = this.sumDraw / n;
    stats.drawCalls = 0;
    stats.instances = 0;
    for (const name of Object.keys(stats.perLayer)) {
      stats.drawCalls += stats.perLayer[name].drawCalls;
      stats.instances += stats.perLayer[name].instances;
    }
    this.resetSums();
    this.sweepAt = now + SWEEP_MS;
    this.opts.onStats?.(statsLine(stats, this.opts.view.scale.x, this.opts.gfx), stats);
  }
}

/**
 * The one-line summary of a sweep (the plan's A.8).
 *
 * The last cell names the BACKEND and the device it found: `gpu:` where
 * WebGPU draws and `gl:` where WebGL2 does. The fall back to WebGL2 is the
 * normal path in Safari and Firefox, so this line is what tells an operator
 * which API drew the picture.
 *
 * `aa1` appears only where `?aa=1` put multisampling on the context, which
 * the client does not ship (R491) and WebGPU does not carry at all. The
 * shipping line therefore never changes: a measurement knob states itself
 * when it is used and stays silent when it is not (R431).
 */
export function statsLine(
  stats: FrameStats, zoom: number,
  gfx: { dpr: number; renderer: string; aa?: boolean; backend?: Backend },
): string {
  const gpu = stats.gpuMs === null ? '-' : `${stats.gpuMs.toFixed(1)}ms`;
  const aa = gfx.aa === true ? ['aa1'] : [];
  const api = gfx.backend === 'webgpu' ? 'gpu' : 'gl';
  return [
    `fast ${stats.fps}fps`,
    `cpu ${stats.frameMs.toFixed(1)}ms (upd ${stats.updateMs.toFixed(1)} draw ${stats.drawMs.toFixed(1)})`,
    `gpu ${gpu}`,
    `dc ${stats.drawCalls}`,
    `inst ${stats.instances}`,
    `z${zoom.toFixed(2)}`,
    `dpr${gfx.dpr}`,
    ...aa,
    `${api}:${gfx.renderer}`,
  ].join(' ');
}
